/**
 * Host rewrite for the image URLs the backend hands out.
 *
 * The API serves one catalogue to every client, so every game cover (and
 * every slide) points at the shared CDN. A client that must not be seen
 * loading another brand's host sets `VITE_IMAGE_HOST_REWRITE` to `from=to`
 * — e.g. `cdn.example.com=img.client.example` — and serves `to` as a proxy
 * of, or a mirror with the same paths as, `from`. Only that one host is
 * touched: covers hosted elsewhere (some providers ship their own) pass
 * through untouched, and so does everything when the variable is unset.
 *
 * Kept pure — no `import.meta.env` — because `vite.config.ts` calls it too,
 * to rewrite the LCP cover it preloads into `index.html`. Each side reads
 * the variable from its own environment and passes the parsed rule in.
 */

/** `[from, to]` hostnames, or `null` when the variable is unset or malformed. */
export type ImageHostRule = [from: string, to: string];

export function parseImageHostRewrite(spec: string | undefined): ImageHostRule | null {
  const [from = '', to = ''] = (spec ?? '').split('=').map((s) => s.trim());
  return from && to ? [from, to] : null;
}

/**
 * Returns `url` with its host replaced when it is exactly `rule[0]`
 * (case-insensitive, no port). Anything else — another host, a relative
 * path, a `data:` URL, an empty string — comes back unchanged. Surrounding
 * whitespace is ignored for the match (the browser would ignore it too) and
 * dropped from a rewritten URL.
 */
export function rewriteImageHost(url: string, rule: ImageHostRule | null): string {
  if (!rule || !url) return url;
  const trimmed = url.trim();
  const scheme = /^https?:\/\//i.exec(trimmed)?.[0];
  if (!scheme) return url;
  const rest = trimmed.slice(scheme.length);
  const hostEnd = rest.search(/[/?#]/);
  const host = hostEnd < 0 ? rest : rest.slice(0, hostEnd);
  if (host.toLowerCase() !== rule[0].toLowerCase()) return url;
  return scheme + rule[1] + rest.slice(host.length);
}
